import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisV, FaEdit, FaShareAlt, FaTrash, FaExternalLinkAlt, FaBell } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import StarRating from './StarRating';
import CategoryIcon from './CategoryIcon';
import { getBookmarkCategory, stripCategoryPrefix } from '../utils/categoryKeywords';
import ReminderCreateDialog from '../reminders/ReminderCreateDialog';
import bookmarkApi from '../services/bookmarkApi';
import '../styles/CompactBookmarkItem.css';

const CompactBookmarkItem = ({
  bookmark,
  onEdit,
  onDelete,
  onShare,
  premiumUser = false
}) => {
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const menuRef = useRef(null);

  // Get category (user-defined or auto-detected)
  const categoryKey = getBookmarkCategory(bookmark);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuOpen]);

  const handleOpenBookmark = () => {
    // window.open() MUSS synchron und zuerst passieren, sonst blockieren
    // Browser das Popup als "nicht durch Nutzeraktion ausgelöst".
    window.open(bookmark.url, '_blank', 'noopener,noreferrer');

    // Fire-and-forget: meldet dem Backend den (ersten) Aufruf, damit dort bei
    // Bedarf ein Plain-Text-Content-Snapshot der Seite angelegt wird (siehe
    // TODO_Archiv_Backend.md Abschnitt 5.1). Nur bei bereits synchronisierten
    // Lesezeichen (echte Backend-ID), nicht bei noch nicht synchronisierten
    // "temp-"-IDs.
    if (bookmark.id && !String(bookmark.id).startsWith('temp-')) {
      bookmarkApi.visitBookmark(bookmark.id);
    }
  };

  const formatDomain = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return url;
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onEdit && onEdit(bookmark);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (window.confirm(t('deleteConfirm') || 'Möchten Sie dieses Lesezeichen wirklich löschen?')) {
      onDelete && onDelete(bookmark.id);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    setMenuOpen(false);

    if (navigator.share) {
      try {
        await navigator.share({
          title: bookmark.title,
          url: bookmark.url
        });
      } catch (err) {
        // User cancelled or share failed
        console.log('Share cancelled or failed');
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(bookmark.url);
        alert(t('urlCopied') || 'URL in Zwischenablage kopiert!');
      } catch {
        console.log('Copy failed');
      }
    }
  };

  const handleReminder = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowReminderDialog(true);
  };

  return (
    <div className={`bookmark-card-modern ${menuOpen ? 'menu-open' : ''}`}>
      {/* Category Icon (replaces Checkbox) */}
      <div className="bookmark-icon-container" onClick={handleOpenBookmark}>
        <CategoryIcon
          category={categoryKey}
          size={52}
        />
      </div>

      {/* Main Content - Clickable */}
      <div className="bookmark-card-content" onClick={handleOpenBookmark}>
        <div className="bookmark-title-row">
          <h3 className="bookmark-title-modern">
            {(bookmark.title || 'Untitled').length > 50 ?
              (bookmark.title || 'Untitled').substring(0, 50) + '...' :
              (bookmark.title || 'Untitled')
            }
          </h3>
          {/* Category Label (optional, shown as small badge) - show original category name */}
          {bookmark.category && (
            <span className="bookmark-category-badge">
              {stripCategoryPrefix(bookmark.category)}
            </span>
          )}
        </div>
        <div className="bookmark-meta-modern">
          <span className="meta-item">
            <span className="meta-icon">🔗</span>
            {formatDomain(bookmark.url)}
          </span>
          {bookmark.rating > 0 && (
            <span className="meta-item bookmark-rating-inline">
              <StarRating
                value={bookmark.rating || 0}
                readOnly={true}
                maxStars={4}
                size="small"
                showEmpty={false}
              />
            </span>
          )}
        </div>
      </div>

      {/* Overflow Menu */}
      <div className="bookmark-overflow-container" ref={menuRef}>
        <button
          className="bookmark-overflow-btn"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          aria-label={t('more') || 'Mehr Optionen'}
          aria-haspopup="true"
          aria-expanded={menuOpen}
        >
          <FaEllipsisV />
        </button>

        {/* Dropdown Menu */}
        {menuOpen && (
          <div className="bookmark-overflow-menu">
            <button className="overflow-menu-item" onClick={handleReminder}>
              <FaBell />
              <span>{t('remindMe') || 'Erinnere mich'}</span>
            </button>
            <button className="overflow-menu-item" onClick={handleOpenBookmark}>
              <FaExternalLinkAlt />
              <span>{t('open') || 'Öffnen'}</span>
            </button>
            <button className="overflow-menu-item" onClick={handleEdit}>
              <FaEdit />
              <span>{t('edit') || 'Bearbeiten'}</span>
            </button>
            <button className="overflow-menu-item" onClick={handleShare}>
              <FaShareAlt />
              <span>{t('share') || 'Teilen'}</span>
            </button>
            <button className="overflow-menu-item overflow-menu-item-danger" onClick={handleDelete}>
              <FaTrash />
              <span>{t('delete') || 'Löschen'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Reminder Create Dialog */}
      {showReminderDialog && (
        <ReminderCreateDialog
          bookmark={bookmark}
          onClose={() => setShowReminderDialog(false)}
          onCreated={() => {
            // Optional: show success notification
          }}
        />
      )}
    </div>
  );
};

export default CompactBookmarkItem;
